import React from 'react'

import { Button, Pill, Tooltip, type TooltipProps } from '../../../../module'
import styles from './_tabs.module.scss'

export type TabItemProps = {
  id: number
  tabName: string
  tag?: number
  subtabs?: boolean
  content?: string | React.JSX.Element
  subrows?: Array<{
    id: number
    tabName: string
    content?: string | React.JSX.Element
  }>
  // Optional prop for disabling tabs
  disabled?: Omit<TooltipProps, 'children'>
  /** Optional prop to add a test id to the Tab for QA testing */
  qaTestId?: string
}

type TabProps = {
  tab: TabItemProps
  active: boolean
  subtabs?: boolean
  onTabClick: () => void
}

const TabLink = React.forwardRef<HTMLLIElement, TabProps>(
  (
    { tab, active, onTabClick, subtabs }: TabProps,
    ref: React.ForwardedRef<HTMLLIElement>,
  ) => {
    // check if tab is disabled
    const disabled = Boolean(tab?.disabled)
    return (
      <li
        ref={ref}
        className={`${styles.singleTab} ${active ? styles.active : ''} ${
          tab.tag ? styles.withPill : ''
        } ${disabled ? styles.tabDisabled : ''}`}
        aria-disabled={disabled}
        data-text={tab.tabName}
        qa-test-id={tab.qaTestId}
      >
        <Button
          as='unstyled'
          className={styles.tab}
          onClick={onTabClick}
          disabled={disabled}
        >
          <span className={styles.tabText}>{tab.tabName}</span>
          {tab.tag ? (
            <Pill
              color={
                active ? (subtabs ? 'dark-purple' : 'purple') : 'medium-purple'
              }
              number={tab.tag}
            />
          ) : null}
        </Button>
      </li>
    )
  },
)

const Tab = React.forwardRef<HTMLLIElement, TabProps>(
  (props, ref: React.ForwardedRef<HTMLLIElement>) => {
    // destructure disabled prop from tab
    const { disabled } = props.tab
    // check if tab is disabled
    return disabled ? (
      <Tooltip {...disabled}>
        <TabLink {...props} ref={ref} />
      </Tooltip>
    ) : (
      // if tab is not disabled
      <TabLink {...props} ref={ref} />
    )
  },
)

Tab.displayName = 'Tab'
export default Tab
